<template>
  <div class="headjoints-detail" style="margin-top: 6em; margin-bottom: 4em">
    <div class="container" style="max-width: 880px">
      <h1 class="text-center mb-3">boehm flute headjoints</h1>
      <p class="text-center text-muted mb-4">
        Wooden headjoints for modern Boehm-system concert flutes — a warmer,
        more colourful voice for your silver flute, without replacing the
        instrument.
      </p>

      <img
        src="@/assets/img/products/headjoint-fan.jpg"
        class="w-100 rounded mb-5"
        alt="Seven wooden Boehm flute headjoints in different woods, fanned out"
      />

      <h2 class="mb-3">Features</h2>
      <ul class="feature-list mb-5">
        <li>
          A fully configurable lip-plate — its height, width, angle around the
          tube, corner rounding and edge rounding are all shaped to your
          preference.
        </li>
        <li>
          A CNC-milled, fully configurable embouchure, exactly as on my Irish
          flutes: front and back undercutting, ovality, a chamfer on the side
          edges, and its length and width.
        </li>
        <li>
          Wood combinations are possible — for example a softer body wood such
          as padauk with a harder lip-plate such as grenadilla.
        </li>
        <li>The tenon is made of brass, or silver on request.</li>
        <li>
          Fitted to your flute's tenon to your specification. <br />
          <span style="font-size: smaller"
            >Please note: if you can't come to me for measuring and fitting, you
            can measure your existing headjoint yourself — but even a few
            hundredths of a millimetre of deviation will result in a headjoint
            that doesn't fit. In that case, your local flute repair shop can do
            the final fitting.</span
          >
        </li>
      </ul>

      <h2 class="mb-3">Construction</h2>
      <p class="mb-5">
        Like my flutes, each headjoint combines hand craftsmanship with
        CNC-controlled precision. The lip-plate and the embouchure — where exact
        geometry matters most — are CNC-milled, then shaped and fine-finished by
        hand.
      </p>

      <h2 class="mb-3">Materials</h2>
      <p class="mb-4">
        The woods are the same as for my flutes — grenadilla<CitesInfoComponent />,
        olive and other close-grained hardwoods. For a headjoint they can be
        combined: a softer, warmer body with a harder, more precise lip-plate.
      </p>
      <p class="mb-5">The tenon is brass as standard, or silver on request.</p>

      <h2 class="mb-3">Prices</h2>
      <p>
        There is no online shop here — to order, just
        <LinkComponent href="/#contact" link-text="contact me" />.
      </p>
      <table class="table align-middle">
        <tbody>
          <tr>
            <th scope="row" class="fw-normal">
              Headjoint — full olive body, or on request with a harder-wood
              lip-plate
            </th>
            <td class="text-end">790&nbsp;€</td>
          </tr>
          <tr>
            <th scope="row" class="fw-normal">Full grenadilla or other wood</th>
            <td class="text-end">by arrangement</td>
          </tr>
          <tr>
            <th scope="row" class="fw-normal">
              Silver tenon (instead of brass)
            </th>
            <td class="text-end">+&nbsp;150–200&nbsp;€*</td>
          </tr>
        </tbody>
      </table>
      <p class="text-muted mb-5">
        *The silver surcharge depends on the current silver price.
      </p>

      <h2 class="mb-3">Gallery</h2>
      <ImageCarouselComponent
        id="headjointGallery"
        :images="galleryImages"
        class="mb-5"
      />

      <p class="text-center">
        <LinkComponent
          href="/#contact"
          link-text="Contact me about a headjoint"
        />
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import LinkComponent from "@/components/util/LinkComponent.vue";
import ImageCarouselComponent from "@/components/util/ImageCarouselComponent.vue";
import CitesInfoComponent from "@/components/general/CitesInfoComponent.vue";
import full from "@/assets/img/products/gallery/headjoint-full.jpg";
import lipplateCombo from "@/assets/img/products/gallery/headjoint-lipplate-combo.jpg";
import lipplate from "@/assets/img/products/gallery/headjoint-lipplate.jpg";
import crown from "@/assets/img/products/gallery/headjoint-crown.jpg";
import headjoint1Full from "@/assets/img/products/gallery/headjoint1-full.jpg";
import headjoint1Lipplate from "@/assets/img/products/gallery/headjoint1-lipplate.jpg";
import oliveLogo from "@/assets/img/products/gallery/headjoint-olive-logo.jpg";
import oliveEmbouchure from "@/assets/img/products/gallery/headjoint-olive-embouchure.jpg";
import oliveFull from "@/assets/img/products/gallery/headjoint-olive-full.jpg";
import grenadillaPanacoco from "@/assets/img/products/gallery/headjoint-grenadilla-panacoco.jpg";
import grenadillaLipplate from "@/assets/img/products/gallery/headjoint-grenadilla-lipplate.jpg";
import oliveLipplate from "@/assets/img/products/gallery/headjoint-olive-lipplate.jpg";
import oliveBrass from "@/assets/img/products/gallery/headjoint-olive-brass.jpg";

export default defineComponent({
  name: "HeadjointsView",
  components: { LinkComponent, ImageCarouselComponent, CitesInfoComponent },
  data() {
    return {
      galleryImages: [
        {
          src: full,
          caption: "Complete headjoint (padauk body, panacoco lip-plate)",
        },
        {
          src: headjoint1Full,
          caption: "Complete headjoint in panacoco with a brass tenon",
        },
        {
          src: lipplateCombo,
          caption: "Harder panacoco lip-plate on a softer padauk body",
        },
        {
          src: lipplate,
          caption: "Hand-milled, fully configurable lip-plate (panacoco)",
        },
        {
          src: headjoint1Lipplate,
          caption: "Lip-plate and embouchure in panacoco",
        },
        { src: crown, caption: "Crown with adjustable tuning cork" },
        {
          src: oliveLogo,
          caption: "An olive headjoint with the ironwood maker's mark",
        },
        {
          src: oliveEmbouchure,
          caption: "The embouchure of an olive headjoint",
        },
        {
          src: oliveFull,
          caption: "A complete olive headjoint with a brass tenon",
        },
        {
          src: grenadillaPanacoco,
          caption: "A grenadilla headjoint with a panacoco lip-plate",
        },
        {
          src: grenadillaLipplate,
          caption: "The panacoco lip-plate on a grenadilla body, close up",
        },
        {
          src: oliveLipplate,
          caption: "A panacoco lip-plate on an olive headjoint",
        },
        {
          src: oliveBrass,
          caption: "A complete olive headjoint with brass tenon and rings",
        },
      ],
    };
  },
});
</script>

<style scoped>
.feature-list li {
  margin-bottom: 0.6rem;
}
</style>

<style src="@/assets/css/style.css"></style>
